const ORGANIZATION_NAME = "Abox Agency";
const BASE_URL = "https://abox.agency";
const DEFAULT_IMAGE = `${BASE_URL}/assets/image/meta_og_image.jpg`;
const LOGO_URL = `${BASE_URL}/assets/image/logo.jpg`;
const SAME_AS = [
    "https://www.linkedin.com/company/aboxagency/",
    "https://www.instagram.com/aboxagency/",
    "https://x.com/aBox_Agency/",
    "https://www.behance.net/aboxagency",
    "https://dribbble.com/aboxagency",
    "https://www.shopify.com/partners/directory/partner/niqox1"
];

function organizationEntity() {
    return {
        "@type": "Organization",
        name: ORGANIZATION_NAME,
        url: BASE_URL,
        logo: LOGO_URL,
        image: DEFAULT_IMAGE,
        sameAs: SAME_AS,
        address: {
            "@type": "PostalAddress",
            streetAddress: "Orbit-1 by Pramukh, Punagam, Magob",
            addressLocality: "Surat",
            addressRegion: "Gujarat",
            postalCode: "395010",
            addressCountry: "IN"
        },
        contactPoint: {
            "@type": "ContactPoint",
            telephone: "+91-7046216489",
            contactType: "sales",
            availableLanguage: ["English", "Hindi", "Gujarati"]
        }
    };
}

export function getOrganizationSchema() {
    return {
        "@context": "https://schema.org",
        ...organizationEntity()
    };
}

/** Ensure JSON-LD is a single object with @context (arrays become @graph). */
export function normalizeJsonLd(schema) {
    if (!schema) return null;

    if (Array.isArray(schema)) {
        return {
            "@context": "https://schema.org",
            "@graph": schema.map((item) => {
                if (!item || typeof item !== "object") return item;
                const { "@context": _ctx, ...rest } = item;
                return rest;
            })
        };
    }

    if (typeof schema === "object") {
        return schema["@context"]
            ? schema
            : { "@context": "https://schema.org", ...schema };
    }

    return null;
}

export function buildServiceSchema({
    name,
    url,
    description,
    image = DEFAULT_IMAGE,
    serviceType = [],
}) {
    return {
        "@context": "https://schema.org",
        "@type": "Service",
        name,
        url,
        description,
        image,
        provider: {
            "@type": "Organization",
            name: ORGANIZATION_NAME,
            url: BASE_URL,
            logo: LOGO_URL
        },
        areaServed: ["IN", "US", "GB", "AE", "AU"],
        serviceType
    };
}

export function buildCollectionPageSchema({
    name,
    url,
    description,
    hasPart = [],
    image = DEFAULT_IMAGE,
}) {
    return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name,
        url,
        description,
        image,
        isPartOf: {
            "@type": "WebSite",
            name: ORGANIZATION_NAME,
            url: BASE_URL
        },
        about: {
            "@type": "Organization",
            name: ORGANIZATION_NAME,
            url: BASE_URL
        },
        hasPart
    };
}

export function buildContactPageSchema({
    name,
    url,
    description,
    image = DEFAULT_IMAGE,
}) {
    return {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name,
        url,
        description,
        image,
        mainEntity: {
            "@type": "Organization",
            name: ORGANIZATION_NAME,
            url: BASE_URL,
            logo: LOGO_URL,
            sameAs: SAME_AS,
            address: {
                "@type": "PostalAddress",
                streetAddress: "Orbit-1 by Pramukh, Punagam, Magob",
                addressLocality: "Surat",
                addressRegion: "Gujarat",
                postalCode: "395010",
                addressCountry: "IN"
            },
            contactPoint: {
                "@type": "ContactPoint",
                telephone: "+91-7046216489",
                contactType: "sales",
                availableLanguage: ["English", "Hindi", "Gujarati"]
            }
        }
    };
}

export function buildAboutPageSchema({
    name,
    url,
    description,
    image = DEFAULT_IMAGE,
}) {
    return {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name,
        url,
        description,
        image,
        mainEntity: organizationEntity()
    };
}

export function buildWebPageSchema({
    name,
    url,
    description,
    image = DEFAULT_IMAGE,
}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name,
        url,
        description,
        image,
        isPartOf: {
            "@type": "WebSite",
            name: ORGANIZATION_NAME,
            url: BASE_URL
        },
        publisher: organizationEntity()
    };
}

export function buildCreativeWorkSchema({
    name,
    url,
    description,
    image = DEFAULT_IMAGE,
    keywords = [],
}) {
    return {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        name,
        url,
        description,
        image,
        keywords,
        creator: {
            "@type": "Organization",
            name: ORGANIZATION_NAME,
            url: BASE_URL
        }
    };
}

/**
 * JobPosting for /career-details/<jobUrl>.
 *
 * Google Jobs requires: title, description, datePosted, hiringOrganization,
 * jobLocation. `description` must be an HTML string covering the role — a bare
 * one-liner gets the posting ignored, so the page's description, responsibilities,
 * experience, skills and attributes are stitched together here rather than
 * passing the summary alone.
 *
 * validThrough is optional but strongly recommended: without it Google may drop
 * the posting after ~30 days, and a posting that outlives the opening is worse
 * than no posting at all. Both dates live in src/data/career.js.
 */
export function buildJobPostingSchema({
    title,
    url,
    description,
    responsibilities = "",
    experience = "",
    skills = "",
    attributes = "",
    datePosted,
    validThrough,
    employmentType = "FULL_TIME",
}) {
    const section = (heading, html) =>
        html ? `<h3>${heading}</h3>${html}` : "";

    const fullDescription = [
        description ? `<p>${description}</p>` : "",
        section("Responsibilities", responsibilities),
        experience ? `<h3>Experience</h3><p>${experience}</p>` : "",
        section("Skills", skills),
        section("Attributes", attributes),
    ]
        .filter(Boolean)
        .join("");

    const schema = {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        title,
        url,
        description: fullDescription,
        datePosted,
        employmentType,
        directApply: true,
        hiringOrganization: {
            "@type": "Organization",
            name: ORGANIZATION_NAME,
            sameAs: BASE_URL,
            url: BASE_URL,
            logo: LOGO_URL
        },
        jobLocation: {
            "@type": "Place",
            address: {
                "@type": "PostalAddress",
                streetAddress: "Orbit-1 by Pramukh, Punagam, Magob",
                addressLocality: "Surat",
                addressRegion: "Gujarat",
                postalCode: "395010",
                addressCountry: "IN"
            }
        }
    };

    // Omit rather than emit an empty value — Google treats a present-but-blank
    // validThrough as an error, where an absent one is merely a missed hint.
    if (validThrough) schema.validThrough = validThrough;

    return schema;
}

export function buildBlogPostingSchema({
    headline,
    url,
    description,
    image = DEFAULT_IMAGE,
    datePublished,
    dateModified,
    authorName = ORGANIZATION_NAME,
    keywords,
}) {
    const schema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline,
        url,
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": url
        },
        description,
        image,
        author: {
            "@type": "Person",
            name: authorName
        },
        publisher: {
            "@type": "Organization",
            name: ORGANIZATION_NAME,
            url: BASE_URL,
            logo: {
                "@type": "ImageObject",
                url: LOGO_URL
            }
        },
        isPartOf: {
            "@type": "Blog",
            name: `${ORGANIZATION_NAME} Blog`,
            url: `${BASE_URL}/blog`
        }
    };

    if (datePublished) schema.datePublished = datePublished;
    if (dateModified) schema.dateModified = dateModified;
    else if (datePublished) schema.dateModified = datePublished;
    if (keywords) schema.keywords = keywords;

    return schema;
}

export function stringifySchema(schema) {
    return JSON.stringify(normalizeJsonLd(schema) || schema);
}

export { BASE_URL, DEFAULT_IMAGE, LOGO_URL, ORGANIZATION_NAME };